/**
 * Disconnect Confirmation Dialog (Story 4.5, Task 7.4)
 *
 * Shown when a user disconnects the last Google integration sharing
 * the same OAuth token. Offers two options:
 * - Keep Google login saved (just remove the integration row)
 * - Remove everything (revoke the OAuth token at Google)
 */

import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { X, AlertTriangle } from "lucide-react";
import { Button } from "@provenleap/ui";

interface DisconnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platformName: string;
  affectedServices: string[];
  onKeepAuth: () => void;
  onRevokeAll: () => void;
  isLoading?: boolean;
}

export function DisconnectDialog({
  open,
  onOpenChange,
  platformName,
  affectedServices,
  onKeepAuth,
  onRevokeAll,
  isLoading = false,
}: DisconnectDialogProps) {
  const [selectedAction, setSelectedAction] = useState<
    "keep" | "revoke" | null
  >(null);
  const prefersReducedMotion = useReducedMotion();

  const instant = { duration: 0 };
  const animationTransition = prefersReducedMotion
    ? instant
    : { duration: 0.2, ease: "easeOut" as const };

  function handleConfirm() {
    if (selectedAction === "keep") {
      onKeepAuth();
    } else if (selectedAction === "revoke") {
      onRevokeAll();
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={animationTransition}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border-[0.5px] border-input bg-background p-6 shadow-lg"
                initial={
                  prefersReducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, scale: 0.95 }
                }
                animate={
                  prefersReducedMotion
                    ? { opacity: 1 }
                    : { opacity: 1, scale: 1 }
                }
                exit={
                  prefersReducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, scale: 0.95 }
                }
                transition={animationTransition}
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <DialogPrimitive.Title className="text-base font-semibold leading-none tracking-tight">
                      Disconnect {platformName} Account?
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="mt-1.5 text-sm text-muted-foreground">
                      This is the last connection using this {platformName} account.
                      Choose how to proceed.
                    </DialogPrimitive.Description>
                  </div>
                  <DialogPrimitive.Close
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </DialogPrimitive.Close>
                </div>

                {/* Affected services */}
                {affectedServices.length > 0 && (
                  <div className="mb-4 rounded-md bg-muted/50 p-3 text-sm">
                    <p className="font-medium mb-1">
                      Services using this connection:
                    </p>
                    <ul className="list-disc pl-4 text-muted-foreground">
                      {affectedServices.map((service) => (
                        <li key={service}>{service}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Options */}
                <div className="space-y-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setSelectedAction("keep")}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      selectedAction === "keep"
                        ? "border-primary bg-primary/5"
                        : "border-input hover:bg-muted/30"
                    }`}
                    disabled={isLoading}
                  >
                    <p className="text-sm font-medium">
                      Keep {platformName} login saved
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Authorization preserved for instant reconnection.
                      Disconnect {platformName} only.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedAction("revoke")}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      selectedAction === "revoke"
                        ? "border-destructive bg-destructive/5"
                        : "border-input hover:bg-muted/30"
                    }`}
                    disabled={isLoading}
                  >
                    <p className="text-sm font-medium text-destructive">
                      Remove everything
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Revokes ProvenLeap's access to your {platformName} account.
                      Requires re-sign-in to reconnect.
                    </p>
                  </button>
                </div>

                {/* Footer */}
                <p className="text-xs text-muted-foreground mb-4">
                  Previously synced data is not affected.
                </p>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <DialogPrimitive.Close asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </DialogPrimitive.Close>
                  <Button
                    variant={
                      selectedAction === "revoke" ? "destructive" : "default"
                    }
                    size="sm"
                    onClick={handleConfirm}
                    disabled={!selectedAction || isLoading}
                  >
                    {isLoading
                      ? "Disconnecting..."
                      : selectedAction === "revoke"
                        ? "Revoke Access"
                        : "Disconnect"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
